import { useCallback } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';

export const StyledKey = styled(Typography)(({ theme, keyWidth, keyMinWidth }) => ({
  color: theme.palette.text.secondary,
  textTransform: 'capitalize',
  width: keyWidth,
  minWidth: keyMinWidth,
  whiteSpace: 'nowrap'
}));

export const StyledValue = styled(Typography)(({ theme, valueAltColor, valueMaxWidth, oneLinerValue }) => ({
  color: valueAltColor || theme.palette.text.primary,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  marginLeft: theme.spacing(0.5),
  maxWidth: valueMaxWidth,
  cursor: 'default'
}));

function NRKeyValueDataDisplay({
  uniqueKey,
  dataKey,
  value,
  keyWidth = '40%',
  minWidth = 168,
  valueMaxWidth = 100,
  showDash,
  showTooltip = false,
  tooltipValue,
  wrapLine = false,
  hasCopy = false,
  valueAltColor,
  endAdornment,
  setIconLeft = false,
  sx = {}
}) {
  const { enqueueSnackbar } = useSnackbar();
  const valuePrefix = showDash ? ' — ' : '';
  const key = uniqueKey || dataKey + value;

  const onCopyThumbprint = useCallback(
    (title, value) => {
      navigator.clipboard.writeText(value);
      enqueueSnackbar(`${title} copied!`, {
        variant: 'success',
        preventDuplicate: true,
        autoHideDuration: 1000
      });
    },
    [enqueueSnackbar]
  );

  return (
    <Box
      key={key}
      sx={{
        display: 'flex',
        mx: 0,
        my: 0.5,
        '&:first-child': {
          marginTop: 0
        },
        ...sx
      }}
    >
      {!wrapLine ? (
        <>
          <StyledKey variant="subtitle2" keyWidth={keyWidth} keyMinWidth={minWidth} children={dataKey + valuePrefix} />
          <Tooltip
            title={showTooltip ? tooltipValue || value : null}
            slotProps={{
              tooltip: {
                sx: {
                  maxHeight: 400
                }
              }
            }}
          >
            <StyledValue variant="body2" valueAltColor={valueAltColor} valueMaxWidth={valueMaxWidth}>
              {value}
            </StyledValue>
          </Tooltip>
          {hasCopy && (
            <StyledValue component={'div'} variant="body2" valueAltColor={valueAltColor} valueMaxWidth={valueMaxWidth}>
              <FileCopyOutlinedIcon
                sx={{ width: '0.875rem', height: '0.875rem', marginLeft: 0.75, marginBottom: -0.25, cursor: 'pointer', color: 'palette.text.icon' }}
                onClick={() => onCopyThumbprint(dataKey, value)}
              />
            </StyledValue>
          )}
          {endAdornment && <Box sx={{ marginLeft: setIconLeft ? 1 : 'auto', marginRight: setIconLeft ? 'auto' : 1 }}>{endAdornment}</Box>}
        </>
      ) : (
        <Box>
          <StyledKey variant="subtitle2" keyWidth={keyWidth} keyMinWidth={minWidth} children={dataKey + valuePrefix} />
          <Tooltip title={showTooltip ? tooltipValue || value : ''}>
            <Typography variant="body2" color={!!valueAltColor ? valueAltColor : 'text.secondary'} component={'span'} sx={{ ml: 0.5 }}>
              {value}
            </Typography>
          </Tooltip>
          {hasCopy && (
            <StyledValue component={'div'} variant="body2" valueAltColor={valueAltColor} valueMaxWidth={valueMaxWidth}>
              <FileCopyOutlinedIcon
                sx={{ width: '0.875rem', height: '0.875rem', marginLeft: 0.75, marginBottom: -0.25, cursor: 'pointer', color: 'palette.text.icon' }}
                className={classes.icon}
                onClick={() => onCopyThumbprint(dataKey, value)}
              />
            </StyledValue>
          )}
        </Box>
      )}
    </Box>
  );
}

export default NRKeyValueDataDisplay;
